/**
 * Calendar — Fias AI UI plane.
 *
 * The assistant reaches the calendar on two planes and this file is only the
 * second one:
 *
 * - SERVER plane (`packages/entities/src/ai-tools/calendar.ts` + the bindings
 *   in `services/api-service/src/routes/arches/calendar/fias-ai-tools.ts`) owns
 *   everything that touches stored data — reading, creating, updating and
 *   deleting events, tasks and scheduling links. Those tools work whether or
 *   not the arche is open, dispatch through `invokeVaultEntity`, and carry
 *   declared risk tiers and confirmation gates.
 * - UI plane (this file) owns the SCREEN: which view is showing, what date it
 *   is parked on, which dialog is open, and the user's local display settings.
 *
 * The split is a hard rule (`docs/arche-ai-interface.md`): a UI action performs
 * pure client-state manipulation and nothing else. An earlier version of this
 * hook called `api.createCalendarEvent`, `api.deleteCalendarEvent` and friends
 * directly from the browser, which put server mutations outside the risk-tier
 * and confirmation machinery entirely. Those actions are gone — the server
 * tools do that work now, and correctly.
 *
 * Registered by CalendarMainPage via `useRegisterArcheAI`, so the actions here
 * are offered to the assistant as `ui__*` tools only while the calendar is on
 * screen.
 */

import { useCallback, useMemo } from 'react';
import type { ArcheAICapabilities } from '@/components/fias-ai';
import type {
  CalendarCollectionDetail,
  CalendarEventInstance,
  SchedulableTaskDetail,
} from '@/lib/api';
import { ARCHE_ID } from '../constants';
import type { CalendarEventInput, CalendarSettings, CalendarView } from '../types';

/** Views the assistant may switch to — mirrors `CalendarView`. */
const VIEWS: CalendarView[] = ['month', 'week', 'day', 'agenda'];

/**
 * Feature switches the assistant may flip, keyed by the name it uses.
 *
 * An explicit map rather than "any key that exists in settings": the settings
 * object also holds structural state (calendar sets, wizard notes, the schema
 * version) that a free-form key/value action would happily corrupt.
 */
const FEATURE_KEYS = {
  task_panel: 'showTaskPanel',
  daily_planning: 'dailyPlanningEnabled',
  evening_review: 'eveningReviewEnabled',
  weather: 'showWeather',
  conflict_warnings: 'showConflictWarnings',
  dim_past_events: 'dimPastEvents',
  weekends: 'showWeekends',
  week_numbers: 'showWeekNumbers',
  toolbar_tasks: 'showToolbarTasks',
  toolbar_focus_time: 'showToolbarFocusTime',
  toolbar_scheduling_links: 'showToolbarSchedulingLinks',
  toolbar_find_time: 'showToolbarFindTime',
  toolbar_analytics: 'showToolbarAnalytics',
  toolbar_import: 'showToolbarImport',
} as const satisfies Record<string, keyof CalendarSettings>;

type FeatureName = keyof typeof FEATURE_KEYS;

/** Scalar display preferences the assistant may set, with their value ranges. */
const DISPLAY_SETTINGS = {
  time_format: { key: 'timeFormat', values: ['12h', '24h'] },
  temperature_unit: { key: 'temperatureUnit', values: ['fahrenheit', 'celsius'] },
  week_start_day: { key: 'weekStartDay', values: [0, 1] },
  default_view: { key: 'defaultView', values: VIEWS },
  time_grid_density: { key: 'timeGridDensity', values: ['compact', 'default', 'comfortable'] },
  event_color_priority: { key: 'eventColorPriority', values: ['event', 'calendar'] },
  agenda_grouping: { key: 'agendaGrouping', values: ['day', 'calendar', 'flat'] },
} as const satisfies Record<string, { key: keyof CalendarSettings; values: readonly unknown[] }>;

type DisplaySettingName = keyof typeof DISPLAY_SETTINGS;

/** Numeric preferences, with the bounds the arche's own settings UI enforces. */
const NUMERIC_SETTINGS = {
  working_hours_start: { key: 'workingHoursStart', min: 0, max: 23 },
  working_hours_end: { key: 'workingHoursEnd', min: 1, max: 24 },
  default_event_duration_minutes: { key: 'defaultEventDuration', min: 5, max: 480 },
  snap_interval_minutes: { key: 'snapIntervalMinutes', min: 5, max: 60 },
  agenda_days_ahead: { key: 'agendaDaysAhead', min: 7, max: 365 },
  month_view_max_events: { key: 'monthViewMaxEvents', min: 0, max: 10 },
  min_break_between_events: { key: 'minBreakBetweenEvents', min: 0, max: 120 },
} as const satisfies Record<string, { key: keyof CalendarSettings; min: number; max: number }>;

type NumericSettingName = keyof typeof NUMERIC_SETTINGS;

export interface CalendarAiSearchControls {
  query: string;
  setQuery: (query: string) => void;
  clear: () => void;
}

export interface UseCalendarAIOptions {
  settings: CalendarSettings;
  updateSettings: (patch: Partial<CalendarSettings>) => void;
  view: CalendarView;
  setView: (view: CalendarView) => void;
  currentDate: Date;
  setCurrentDate: (date: Date) => void;
  /** The window the calendar is currently showing, for the state snapshot. */
  visibleRange: { start: Date; end: Date };
  calendars: CalendarCollectionDetail[];
  visibleEvents: CalendarEventInstance[];
  tasks: SchedulableTaskDetail[];
  search: CalendarAiSearchControls;
  /** True when the left panel is on screen (docked or as a mobile overlay). */
  sidebarVisible: boolean;
  onToggleSidebar: () => void;
  /** Opens the full event dialog with fields pre-filled for the user to finish. */
  openEventComposer: (initial: Partial<CalendarEventInput>) => void;
  openFindTime: () => void;
  openSchedulingLinks: () => void;
  openDailyPlanning: () => void;
  openEveningReview: () => void;
  openFocusTime: () => void;
  openImport: () => void;
  /** Refetch after the assistant writes calendar data through a server tool. */
  refetchEvents: () => void;
  refetchTasks?: () => void;
}

/** How many events the snapshot describes before it stops enumerating. */
const SNAPSHOT_EVENT_LIMIT = 60;
const SNAPSHOT_TASK_LIMIT = 25;

const ACTIONS: ArcheAICapabilities['actions'] = [
  // --- Navigation -----------------------------------------------------------
  {
    id: 'switch_view',
    description:
      'Switch the calendar to a different view. Use when the user asks to see their month, ' +
      'week, day, or an agenda list. Does not change which date is shown.',
    parameterSchema: {
      type: 'object',
      required: ['view'],
      additionalProperties: false,
      properties: { view: { type: 'string', enum: VIEWS } },
    },
    category: 'navigation',
  },
  {
    id: 'navigate_to_date',
    description:
      'Move the calendar to a date, keeping the current view. Pass an ISO-8601 date ' +
      "(YYYY-MM-DD) or datetime. The user's current date and timezone are in the system " +
      'prompt — never guess the year.',
    parameterSchema: {
      type: 'object',
      required: ['date'],
      additionalProperties: false,
      properties: { date: { type: 'string', maxLength: 40 } },
    },
    category: 'navigation',
  },
  {
    id: 'show_event_search',
    description:
      "Put a search term in the calendar's search box so the user sees the matching events " +
      'on screen. This drives the UI only — to READ matching events and answer a question ' +
      'about them, use the calendar_list_events server tool instead.',
    parameterSchema: {
      type: 'object',
      required: ['query'],
      additionalProperties: false,
      properties: { query: { type: 'string', minLength: 1, maxLength: 200 } },
    },
    category: 'navigation',
  },
  {
    id: 'clear_event_search',
    description: 'Clear the calendar search box and return to the normal view.',
    parameterSchema: { type: 'object', additionalProperties: false, properties: {} },
    category: 'navigation',
  },

  // --- Composing ------------------------------------------------------------
  {
    id: 'open_event_composer',
    description:
      'Open the new-event dialog with fields pre-filled, leaving the user to review and save. ' +
      'Use this when the user wants to fill in the rest themselves, or when details are ' +
      'uncertain. To create an event outright, use the calendar_create_event server tool — ' +
      'it works whether or not the calendar is open and records the write properly.',
    parameterSchema: {
      type: 'object',
      additionalProperties: false,
      properties: {
        title: { type: 'string', maxLength: 300 },
        startAt: { type: 'string', maxLength: 40, description: 'ISO-8601 with offset' },
        endAt: { type: 'string', maxLength: 40, description: 'ISO-8601 with offset' },
        isAllDay: { type: 'boolean' },
        description: { type: 'string', maxLength: 4000 },
        location: { type: 'string', maxLength: 500 },
      },
    },
    category: 'events',
  },

  // --- Productivity dialogs -------------------------------------------------
  {
    id: 'open_find_time',
    description:
      'Open Find a Time, which searches for meeting slots that work for the user AND other ' +
      'people who have shared their calendar with them. Use it for any multi-person ' +
      'scheduling question — the calendar_check_availability server tool only sees the ' +
      "user's own calendar.",
    parameterSchema: { type: 'object', additionalProperties: false, properties: {} },
    category: 'productivity',
  },
  {
    id: 'open_scheduling_links',
    description:
      'Open the Scheduling Links manager, where the user reviews their public booking links ' +
      'and the bookings made against them.',
    parameterSchema: { type: 'object', additionalProperties: false, properties: {} },
    category: 'productivity',
  },
  {
    id: 'open_daily_planning',
    description:
      "Open the Plan My Day wizard: review yesterday, walk through today's schedule, and set " +
      "the day's focus priorities.",
    parameterSchema: { type: 'object', additionalProperties: false, properties: {} },
    category: 'productivity',
  },
  {
    id: 'open_evening_review',
    description:
      'Open the Evening Review wizard: a summary of the day, a chance to reschedule what was ' +
      'missed, and a reflection note.',
    parameterSchema: { type: 'object', additionalProperties: false, properties: {} },
    category: 'productivity',
  },
  {
    id: 'open_focus_time',
    description:
      'Open the Focus Time dialog, where the user generates or clears blocks of protected ' +
      'time around their existing commitments.',
    parameterSchema: { type: 'object', additionalProperties: false, properties: {} },
    category: 'productivity',
  },
  {
    id: 'open_import',
    description: 'Open the import dialog for loading events from an .ics file.',
    parameterSchema: { type: 'object', additionalProperties: false, properties: {} },
    category: 'productivity',
  },

  // --- Layout and preferences ----------------------------------------------
  {
    id: 'toggle_sidebar',
    description:
      'Show or hide the left panel (mini month, calendar list, tasks). On a narrow screen the ' +
      'panel opens over the calendar rather than beside it.',
    parameterSchema: { type: 'object', additionalProperties: false, properties: {} },
    category: 'layout',
  },
  {
    id: 'set_feature',
    description:
      "Turn one of the calendar's optional features on or off. The toolbar_* features control " +
      'whether that button appears in the toolbar; turning toolbar_tasks off also hides the ' +
      'task panel.',
    parameterSchema: {
      type: 'object',
      required: ['feature', 'enabled'],
      additionalProperties: false,
      properties: {
        feature: { type: 'string', enum: Object.keys(FEATURE_KEYS) },
        enabled: { type: 'boolean' },
      },
    },
    category: 'settings',
  },
  {
    id: 'set_display_preference',
    description:
      "Set one of the calendar's display preferences to one of its allowed values. These are " +
      "stored per browser, not on the user's account.",
    parameterSchema: {
      type: 'object',
      required: ['preference', 'value'],
      additionalProperties: false,
      properties: {
        preference: { type: 'string', enum: Object.keys(DISPLAY_SETTINGS) },
        value: {
          description: 'One of the values listed for that preference in the state snapshot',
        },
      },
    },
    category: 'settings',
  },
  {
    id: 'set_numeric_preference',
    description:
      "Set one of the calendar's numeric preferences. Hours are 0–23 in local time; the " +
      'allowed range for each is in the state snapshot.',
    parameterSchema: {
      type: 'object',
      required: ['preference', 'value'],
      additionalProperties: false,
      properties: {
        preference: { type: 'string', enum: Object.keys(NUMERIC_SETTINGS) },
        value: { type: 'number' },
      },
    },
    category: 'settings',
  },
];

const ARCHE_DESCRIPTION =
  "The user's calendar, currently open on screen. Events, calendar collections, schedulable " +
  'tasks, focus time, scheduling links and .ics import/export live here. ' +
  'The ui__ actions on this plane drive the SCREEN — which view and date are showing, which ' +
  'dialog is open, and the local display preferences. Everything that reads or changes stored ' +
  'calendar data goes through the calendar_* server tools, which work whether or not this ' +
  "arche is open. Prefer a server tool to answer a question about the user's schedule, and a " +
  'ui__ action to put something in front of them.';

export function useCalendarAI(options: UseCalendarAIOptions): ArcheAICapabilities {
  const {
    settings,
    updateSettings,
    view,
    setView,
    currentDate,
    setCurrentDate,
    visibleRange,
    calendars,
    visibleEvents,
    tasks,
    search,
    sidebarVisible,
    onToggleSidebar,
    openEventComposer,
    openFindTime,
    openSchedulingLinks,
    openDailyPlanning,
    openEveningReview,
    openFocusTime,
    openImport,
    refetchEvents,
    refetchTasks,
  } = options;

  const getStateSnapshot = useCallback((): Record<string, unknown> => {
    const shown = visibleEvents.slice(0, SNAPSHOT_EVENT_LIMIT);
    const openTasks = tasks
      .filter((t) => t.status !== 'completed' && t.status !== 'cancelled')
      .slice(0, SNAPSHOT_TASK_LIMIT);

    return {
      // `currentDate` carries the browser's offset so the model can build
      // timestamps in the user's own timezone rather than guessing at UTC.
      view,
      currentDate: currentDate.toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      visibleRange: {
        start: visibleRange.start.toISOString(),
        end: visibleRange.end.toISOString(),
      },
      calendars: calendars.map((c) => ({
        id: c.id,
        name: c.name,
        color: c.color,
        isDefault: c.isDefault,
        isVisible: c.isVisible,
      })),
      visibleEvents: shown.map((e) => ({
        eventId: e.eventId,
        title: e.title,
        startAt: e.startAt,
        endAt: e.endAt,
        isAllDay: e.isAllDay,
        calendarId: e.calendarId,
        location: e.location || null,
      })),
      visibleEventsTruncated: visibleEvents.length > shown.length,
      openTasks: openTasks.map((t) => ({
        taskId: t.id,
        title: t.title,
        deadline: t.deadline,
        estimatedMinutes: t.estimatedMinutes,
        priority: t.priority,
        status: t.status,
      })),
      ui: {
        sidebarVisible,
        searchQuery: search.query || null,
      },
      features: Object.fromEntries(
        Object.entries(FEATURE_KEYS).map(([name, key]) => [name, settings[key]]),
      ),
      displayPreferences: Object.fromEntries(
        Object.entries(DISPLAY_SETTINGS).map(([name, def]) => [
          name,
          { value: settings[def.key], allowed: def.values },
        ]),
      ),
      numericPreferences: Object.fromEntries(
        Object.entries(NUMERIC_SETTINGS).map(([name, def]) => [
          name,
          { value: settings[def.key], min: def.min, max: def.max },
        ]),
      ),
    };
  }, [
    view,
    currentDate,
    visibleRange,
    calendars,
    visibleEvents,
    tasks,
    sidebarVisible,
    search.query,
    settings,
  ]);

  const applyAction = useCallback(
    (actionId: string, params: Record<string, unknown>) => {
      switch (actionId) {
        case 'switch_view': {
          const next = params.view as CalendarView;
          if (!VIEWS.includes(next)) throw new Error(`Unknown view "${String(params.view)}"`);
          setView(next);
          return;
        }
        case 'navigate_to_date': {
          const parsed = new Date(params.date as string);
          if (Number.isNaN(parsed.getTime())) {
            throw new Error(`Could not read "${String(params.date)}" as a date`);
          }
          setCurrentDate(parsed);
          return;
        }
        case 'show_event_search': {
          search.setQuery(String(params.query ?? ''));
          return;
        }
        case 'clear_event_search': {
          search.clear();
          return;
        }
        case 'open_event_composer': {
          const initial: Partial<CalendarEventInput> = {};
          if (typeof params.title === 'string') initial.title = params.title;
          if (typeof params.startAt === 'string') initial.startAt = params.startAt;
          if (typeof params.endAt === 'string') initial.endAt = params.endAt;
          if (typeof params.isAllDay === 'boolean') initial.isAllDay = params.isAllDay;
          if (typeof params.description === 'string') initial.description = params.description;
          if (typeof params.location === 'string') initial.location = params.location;
          openEventComposer(initial);
          return;
        }
        case 'open_find_time':
          openFindTime();
          return;
        case 'open_scheduling_links':
          openSchedulingLinks();
          return;
        case 'open_daily_planning':
          openDailyPlanning();
          return;
        case 'open_evening_review':
          openEveningReview();
          return;
        case 'open_focus_time':
          openFocusTime();
          return;
        case 'open_import':
          openImport();
          return;
        case 'toggle_sidebar':
          onToggleSidebar();
          return;
        case 'set_feature': {
          const name = params.feature as FeatureName;
          const key = FEATURE_KEYS[name];
          if (!key) throw new Error(`Unknown feature "${String(params.feature)}"`);
          updateSettings({ [key]: params.enabled === true } as Partial<CalendarSettings>);
          return;
        }
        case 'set_display_preference': {
          const name = params.preference as DisplaySettingName;
          const def = DISPLAY_SETTINGS[name];
          if (!def) throw new Error(`Unknown preference "${String(params.preference)}"`);
          if (!(def.values as readonly unknown[]).includes(params.value)) {
            throw new Error(
              `"${String(params.value)}" is not a value ${name} accepts (${def.values.join(', ')})`,
            );
          }
          updateSettings({ [def.key]: params.value } as Partial<CalendarSettings>);
          return;
        }
        case 'set_numeric_preference': {
          const name = params.preference as NumericSettingName;
          const def = NUMERIC_SETTINGS[name];
          if (!def) throw new Error(`Unknown preference "${String(params.preference)}"`);
          const value = Number(params.value);
          if (!Number.isFinite(value) || value < def.min || value > def.max) {
            throw new Error(`${name} must be a number between ${def.min} and ${def.max}`);
          }
          updateSettings({ [def.key]: value } as Partial<CalendarSettings>);
          return;
        }
        default:
          throw new Error(`Unknown calendar action "${actionId}"`);
      }
    },
    [
      setView,
      setCurrentDate,
      search,
      openEventComposer,
      openFindTime,
      openSchedulingLinks,
      openDailyPlanning,
      openEveningReview,
      openFocusTime,
      openImport,
      onToggleSidebar,
      updateSettings,
    ],
  );

  const onExternalDataChange = useCallback(
    (domains: string[]) => {
      // The calendar's event and task lists are useState + manual refetch, not
      // React Query, so aiDataSync's cache invalidation cannot reach them.
      if (!domains.includes(ARCHE_ID)) return;
      refetchEvents();
      refetchTasks?.();
    },
    [refetchEvents, refetchTasks],
  );

  return useMemo(
    () => ({
      archeId: ARCHE_ID,
      enabled: true,
      archeDescription: ARCHE_DESCRIPTION,
      actions: ACTIONS,
      getStateSnapshot,
      applyAction,
      onExternalDataChange,
    }),
    [getStateSnapshot, applyAction, onExternalDataChange],
  );
}
